/**
 * @module AmbientModeContext
 * @description Contexto global para el modo ambiente de la aplicación.
 *
 * El modo ambiente se activa automáticamente cuando el usuario permanece inactivo
 * durante `IDLE_TIMEOUT_MS` milisegundos, mostrando un fondo visual animado o de
 * presentación. Se desactiva en cuanto el usuario interactúa de nuevo con la página.
 *
 * Integración con ACSContext:
 * - Se importa de forma dinámica (require) para evitar un acoplamiento fuerte entre módulos.
 * - Si hay una sesión ACS activa (videoconferencia), el timer de inactividad se deshabilita
 *   para no interrumpir la reunión con el modo ambiente.
 *
 * Feature flag: `FEATURES.AMBIENT_MODE` controla si esta funcionalidad está habilitada
 * en el entorno actual (ver `src/config/features.ts`).
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useState,
} from 'react';
import { useIdleTimer, DEFAULT_EVENTS } from '../hooks/useIdleTimer';
import type { AmbientModeContextType } from '../types/ambient';
import { FEATURES } from '../config/features';
import { useAuth } from './AuthContext';

// Intentamos importar useACS de forma opcional para no acoplar fuertemente
// AmbientModeContext a ACSContext. Si no existe o no está en el árbol,
// se ignora silenciosamente.
let _useACS: (() => { activeSession: unknown }) | null = null;
try {
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  const acsModule = require('./ACSContext');
  _useACS = acsModule.useACS;
} catch {
  // ACSContext no disponible — se procede sin la verificación
}

/**
 * Tiempo de inactividad en milisegundos tras el cual se activa el modo ambiente.
 * Actualmente configurado en 5 segundos (valor de desarrollo); en producción
 * debería ajustarse a ~15 minutos (900_000 ms).
 */
const IDLE_TIMEOUT_MS = 5_000; // 15 minutos

/** Contexto interno del modo ambiente — se accede a través de `useAmbientMode()`. */
const AmbientModeContext = createContext<AmbientModeContextType | null>(null);

/**
 * Proveedor del modo ambiente. Gestiona el estado `isAmbient` y configura
 * el timer de inactividad que lo activa/desactiva automáticamente.
 *
 * Condiciones para que el timer esté habilitado:
 * - `FEATURES.AMBIENT_MODE` debe ser `true`.
 * - El usuario debe estar autenticado (`user !== null`).
 * - No debe haber una sesión ACS activa.
 *
 * @param children - Árbol de componentes React a envolver.
 */
export function AmbientModeProvider({ children }: { children: React.ReactNode }) {
  const [isAmbient, setIsAmbient] = useState(false);
  const { user } = useAuth();

  // Verificar si hay sesión ACS activa para deshabilitar el idle timer
  // mientras el usuario está en una sesión de videoconferencia.
  let hasActiveACSSession = false;
  try {
    if (_useACS) {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const acs = _useACS();
      hasActiveACSSession = Boolean(acs?.activeSession);
    }
  } catch {
    // useACS fuera de ACSProvider — ignorar
  }

  /**
   * Activa el modo ambiente. Solo tiene efecto si el feature flag
   * `FEATURES.AMBIENT_MODE` está habilitado.
   */
  const activate = useCallback(() => {
    if (FEATURES.AMBIENT_MODE) setIsAmbient(true);
  }, []);

  /**
   * Desactiva el modo ambiente y vuelve a la vista normal.
   * Se llama automáticamente cuando el usuario interactúa con la página.
   */
  const deactivate = useCallback(() => {
    setIsAmbient(false);
  }, []);

  useIdleTimer({
    timeoutMs: IDLE_TIMEOUT_MS,
    events: DEFAULT_EVENTS,
    onIdle: activate,
    onActive: deactivate,
    // Desactivar si no hay usuario autenticado, AMBIENT_MODE está apagado, o hay sesión ACS activa
    enabled: FEATURES.AMBIENT_MODE && !!user && !hasActiveACSSession,
  });

  return (
    <AmbientModeContext.Provider value={{ isAmbient, activate, deactivate }}>
      {children}
    </AmbientModeContext.Provider>
  );
}

/**
 * Hook para consumir el contexto de modo ambiente desde cualquier componente
 * hijo de `AmbientModeProvider`.
 *
 * @throws Error si se usa fuera de `AmbientModeProvider`.
 * @returns `{ isAmbient, activate, deactivate }` — estado actual y controles del modo ambiente.
 *
 * @example
 * const { isAmbient } = useAmbientMode();
 * if (isAmbient) return <AmbientScreen />;
 */
export function useAmbientMode(): AmbientModeContextType {
  const ctx = useContext(AmbientModeContext);
  if (!ctx) throw new Error('[AMBIENT] useAmbientMode must be used inside AmbientModeProvider');
  return ctx;
}
